/* Смена режима экрана без React (engine-kit, P1): ядро бывшего useStudioMediaMode (studio/use-studio-media-mode.ts), которым теперь
   пользуются и родные формы «Фото» и «Видео» (тонкая обёртка), и кадр движка с режимами (mode-group.tsx).
   Каждый режим — своё приложение сервера. Смена: один запрос за раз; владелец проверяется до GET приложения и после него (свежий
   GET /app/me), приложение должно быть того же экрана; черновик уходящего режима хранится по владелец:приложение, у режима, в
   который вернулись, — его собственный черновик (с transfer — и входы уходящего поверх него); 30 с на всё. Смена владельца
   стирает черновики. Сбой — текст «Не удалось открыть режим. Попробуйте ещё раз.», режим прежний. */
import { endpoints } from '../../../api';
import { studioApi, type StudioApp } from '../studio-api';

export interface ModeState {
  /** Приложение текущего режима */
  active: StudioApp;
  /** Начальные входы текущего режима: его черновик (с transfer — и входы прежнего режима) */
  seed: Record<string, unknown> | undefined;
  switching: boolean;
  error: string;
}
export interface ModeSwitch {
  /** Тот же объект до следующего изменения */
  get(): ModeState;
  subscribe(listener: () => void): () => void;
  /** Монтирование хозяина: снятие отменяет идущую смену, состояние после него не меняется (StrictMode — повторно без вреда) */
  attach(): () => void;
  /** true — режим id стал текущим (или уже был им); false — смена не прошла или уже идёт другая */
  switchMode(id: number, inputs: Record<string, string>, expectedOwner?: number, transfer?: boolean): Promise<boolean>;
}

export const MODE_SWITCH_MS = 30000;
const FAILED = 'Не удалось открыть режим. Попробуйте ещё раз.';

export function createModeSwitch(initialApp: StudioApp, initialInputs: Record<string, unknown> | undefined, screen: string): ModeSwitch {
  let state: ModeState = { active: initialApp, seed: initialInputs, switching: false, error: '' };
  const listeners = new Set<() => void>();
  const drafts = new Map<string, Record<string, string>>();
  let owner: number | null = null,
    flight = false,
    mounted = true,
    request: AbortController | null = null;
  const set = (patch: Partial<ModeState>) => {
    state = { ...state, ...patch };
    for (const listener of [...listeners]) listener();
  };
  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attach() {
      mounted = true;
      return () => {
        mounted = false;
        request?.abort();
      };
    },
    async switchMode(id, inputs, expectedOwner, transfer = false) {
      if (flight) return false;
      if (id === state.active.id) return true;
      flight = true;
      set({ switching: true, error: '' });
      const controller = new AbortController();
      request = controller;
      const timer = window.setTimeout(() => controller.abort(), MODE_SWITCH_MS);
      /* итог применяется одним изменением в конце, как пакет setState бывшего хука: новый режим и switching:false вместе */
      let done: Pick<ModeState, 'active' | 'seed'> | null = null,
        error = '';
      try {
        const me = await endpoints.me();
        if (controller.signal.aborted || !mounted) return false;
        if (!expectedOwner || me.user.id !== expectedOwner || (owner !== null && owner !== me.user.id)) {
          drafts.clear();
          throw new Error('owner_changed');
        }
        owner = me.user.id;
        drafts.set(`${me.user.id}:${state.active.id}`, { ...inputs });
        const app = await studioApi.app(id, controller.signal);
        if (controller.signal.aborted || !mounted) return false;
        if (app.entry.screen !== screen) throw new Error('wrong_screen');
        const fresh = await endpoints.me();
        if (controller.signal.aborted || !mounted) return false;
        if (fresh.user.id !== me.user.id) {
          drafts.clear();
          throw new Error('owner_changed');
        }
        const saved = drafts.get(`${me.user.id}:${id}`);
        done = { active: app, seed: transfer ? { ...saved, ...inputs } : saved };
        return true;
      } catch {
        if (mounted) error = FAILED;
        return false;
      } finally {
        window.clearTimeout(timer);
        flight = false;
        if (request === controller) request = null;
        if (mounted) {
          if (controller.signal.aborted) error = FAILED;
          set({ ...done, ...(error ? { error } : {}), switching: false });
        }
      }
    },
  };
}
